import { CatalystInsightFilterRequest, ChartInsight } from '@api';
import { ChartAssetData, EventsConfig, RangeHorizon } from '@chart';
import { useQueryClient } from '@tanstack/react-query';
import { getCatalystInsightListForChart } from '@terminal/services/insights/catalyst-insights-service';
import { getEarningsInsightsForChart } from '@terminal/services/insights/earnings-insights-service';
import { getKgCompanyCatalystInsightListForChart } from '@terminal/services/insights/kg-insights-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ChartSearchParams } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { fetchFeed } from '@terminal/views/turbo-chart/feed-services/feed';
import { subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

const getStartDate = (endDate: Date, chartHorizon?: RangeHorizon) => {
  switch (chartHorizon) {
    case RangeHorizon.OneDay:
      return subDays(endDate, 1);
    case RangeHorizon.OneWeek:
      return subWeeks(endDate, 1);
    case RangeHorizon.TwoWeeks:
      return subWeeks(endDate, 2);
    case RangeHorizon.OneMonth:
      return subMonths(endDate, 1);
    case RangeHorizon.ThreeMonths:
      return subMonths(endDate, 3);
    case RangeHorizon.OneYear:
      return subYears(endDate, 1);
    case RangeHorizon.ThreeYears:
      return subYears(endDate, 3);
    case RangeHorizon.FiveYears:
      return subYears(endDate, 5);
    case RangeHorizon.TenYears:
      return subYears(endDate, 10);
    case RangeHorizon.SixMonths:
    default:
      return subMonths(endDate, 6);
  }
};

export interface UseChartInsightsProps {
  chartData: ChartAssetData[];
  eventsConfig: EventsConfig;
  chartHorizon?: RangeHorizon;
  hasStaleTime?: boolean;
  isSimpleChart?: boolean;
}

export const useChartInsights = ({
  chartData,
  eventsConfig,
  hasStaleTime = true,
}: UseChartInsightsProps) => {
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const isCatalystGenerationEnabled = useFlags(state =>
    state.getFeatureFlagState('catalyst_generation')
  );
  const [insights, setInsights] = useState<ChartInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const assets = searchParams.getAll(ChartSearchParams.Asset).toString();
  const queryClient = useQueryClient();

  const fetchInsights = async (primaryAsset: ChartAssetData) => {
    const ts = primaryAsset.ts;
    const endDate = new Date(ts[ts.length - 1].time);
    const startDate = getStartDate(endDate);

    const articles = await queryClient.fetchQuery({
      queryKey: ['article-insights', primaryAsset.entity.tag],
      queryFn: () =>
        fetchFeed({
          entityTag: primaryAsset.entity.tag,
          startDate,
          endDate: new Date(ts[ts.length - 1].time),
        }),
      staleTime: hasStaleTime ? 3_600_000 : undefined,
    });

    if (isBasicOrLightUser) {
      return articles.error ? [] : articles.result;
    }

    const toDate = new Date(ts[ts.length - 1].time).toISOString();
    const fromDate = startDate.toISOString();
    const staleTime = hasStaleTime ? 3_600_000 : undefined;

    const payload: CatalystInsightFilterRequest = {
      entities: [primaryAsset.entity.tag],
      categories: ['company'],
      from: fromDate,
      to: toDate,
    };

    const [catalystInsights, earningsInsights] = await Promise.allSettled([
      queryClient.fetchQuery({
        queryKey: [
          isCatalystGenerationEnabled ? 'kg-insights' : 'catalyst-insights',
          primaryAsset.entity.tag,
          fromDate,
          toDate,
        ],
        queryFn: () =>
          isCatalystGenerationEnabled
            ? getKgCompanyCatalystInsightListForChart(payload)
            : getCatalystInsightListForChart(payload),
        staleTime,
      }),
      queryClient.fetchQuery({
        queryKey: [
          'earnings-insights',
          primaryAsset.entity.tag,
          startDate.toISOString(),
          endDate.toISOString(),
          isCatalystGenerationEnabled,
        ],
        queryFn: () =>
          getEarningsInsightsForChart(
            {
              entities: [primaryAsset.entity.tag],
              from: startDate.toISOString(),
              to: new Date(ts[ts.length - 1].time).toISOString(),
            },
            isCatalystGenerationEnabled
          ),
        staleTime: hasStaleTime ? 5 * 60 * 1000 : undefined,
      }),
    ]);

    const articlesResult = articles.error ? [] : articles.result;
    const catalystInsightsResult =
      catalystInsights.status === 'fulfilled'
        ? (catalystInsights.value.data ?? [])
        : [];
    const deduplicatedEarningsInsight =
      earningsInsights.status === 'fulfilled'
        ? (earningsInsights.value ?? [])
        : [];

    return [
      ...articlesResult,
      ...catalystInsightsResult,
      ...deduplicatedEarningsInsight,
    ];
  };

  const getInsights = async () => {
    setInsightsLoading(true);

    const uniqueEntities = new Map<string, ChartAssetData>();
    chartData.forEach(asset => {
      const entityTag = asset.entity.tag;

      if (!uniqueEntities.has(entityTag)) {
        uniqueEntities.set(entityTag, asset);
      }
    });

    const result = await Promise.all(
      [...uniqueEntities.values()].map(fetchInsights)
    );

    setInsights(result.flat());
    setInsightsLoading(false);
  };

  useEffect(() => {
    if (!eventsConfig.TMI) {
      return;
    }

    if (chartData.length) {
      getInsights();
    }
  }, [assets, eventsConfig, chartData]);

  return {
    insights,
    insightsLoading,
    getInsights,
  };
};
